import { useState, useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Lightbulb, Target, MessageSquare, AlertCircle, Loader2, Star, Sparkles } from "lucide-react";
import type { NarrativeAngle } from "@/lib/campaign-draft";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";

interface NarrativeAnglesStepProps {
  brandId: string;
  generationId: string | null;
  selectedAngles: NarrativeAngle[];
  onSelect: (angles: NarrativeAngle[]) => void;
  hasOpportunity: boolean;
  opportunityTitle?: string | null;
}

const MAX_ANGLES = 3;

const FAMILY_COLORS: Record<string, string> = {
  conflict: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  identity: "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400",
  outcome: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
  trust: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  worldview: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  life_fit: "bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400",
};

function averageScore(angle: NarrativeAngle): number | null {
  const scores = [
    angle.resonanceScore,
    angle.proofScore,
    angle.distinctivenessScore,
    angle.emotionalScore,
  ].filter((s): s is number => s !== null);
  if (scores.length === 0) return null;
  return Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
}

function scoreColor(score: number | null) {
  if (score === null) return "text-muted-foreground";
  if (score >= 85) return "text-primary";
  if (score >= 70) return "text-foreground/70";
  return "text-muted-foreground";
}

const PROGRESS_MESSAGES = [
  "Analyzing narrative opportunity…",
  "Generating angle variations…",
  "Scoring resonance and distinctiveness…",
  "Evaluating emotional impact…",
  "Finalizing narrative angles…",
];

function InlineGenerateAnglesPanel({
  generationId,
  opportunityTitle,
  brandId,
}: {
  generationId: string;
  opportunityTitle: string;
  brandId: string;
}) {
  const [isGenerating, setIsGenerating] = useState(false);
  const [progressIndex, setProgressIndex] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  useEffect(() => {
    if (!isGenerating) return;
    const interval = setInterval(() => {
      setProgressIndex((prev) =>
        prev < PROGRESS_MESSAGES.length - 1 ? prev + 1 : prev,
      );
    }, 5000);
    return () => clearInterval(interval);
  }, [isGenerating]);

  const handleGenerate = async () => {
    setError(null);
    setProgressIndex(0);
    setIsGenerating(true);
    try {
      const res = await fetch(
        `/api/narrative-generations/${generationId}/generate-angles`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            selectedOpportunities: [{ title: opportunityTitle }],
          }),
        },
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || "Failed to generate angles");
      }
      queryClient.invalidateQueries({
        queryKey: [`/api/brands/${brandId}/narrative-angles?generation_id=${generationId}`],
      });
      toast({
        title: "Angles ready",
        description: "Narrative angles have been generated. Select up to 3 below.",
      });
    } catch (err: any) {
      setError(err.message || "Failed to generate angles");
    } finally {
      setIsGenerating(false);
    }
  };

  if (isGenerating) {
    return (
      <Card className="p-6 text-center space-y-4" data-testid="card-generating-angles">
        <div className="flex items-center justify-center gap-2">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
          <span className="text-sm font-medium">Generating Narrative Angles</span>
        </div>
        <AnimatePresence mode="wait">
          <motion.p
            key={progressIndex}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            className="text-xs text-muted-foreground"
          >
            {PROGRESS_MESSAGES[progressIndex]}
          </motion.p>
        </AnimatePresence>
        <div className="w-full max-w-xs mx-auto bg-muted rounded-full h-1.5 overflow-hidden">
          <motion.div
            className="h-full bg-primary rounded-full"
            initial={{ width: "5%" }}
            animate={{ width: `${Math.min(15 + progressIndex * 18, 90)}%` }}
            transition={{ duration: 1 }}
          />
        </div>
        <p className="text-[11px] text-muted-foreground/60">
          This typically takes 1-2 minutes
        </p>
      </Card>
    );
  }

  return (
    <Card className="p-6 text-center space-y-3" data-testid="card-no-angles">
      <AlertCircle className="h-5 w-5 text-muted-foreground mx-auto" />
      <p className="text-sm text-muted-foreground">
        No narrative angles found for this generation.
      </p>
      <p className="text-xs text-muted-foreground/70">
        Generate angle variations from the selected opportunity
        <span className="font-medium text-foreground/70"> "{opportunityTitle}"</span>.
      </p>
      {error && (
        <p className="text-xs text-destructive">{error}</p>
      )}
      <Button
        onClick={handleGenerate}
        size="sm"
        className="gap-2"
        data-testid="button-generate-angles"
      >
        <Sparkles className="h-3.5 w-3.5" />
        Generate Narrative Angles
      </Button>
    </Card>
  );
}

export function NarrativeAnglesStep({
  brandId,
  generationId,
  selectedAngles,
  onSelect,
  hasOpportunity,
  opportunityTitle,
}: NarrativeAnglesStepProps) {
  const { data: anglesData, isLoading, isError } = useQuery<NarrativeAngle[]>({
    queryKey: [`/api/brands/${brandId}/narrative-angles?generation_id=${generationId}`],
    enabled: !!brandId && !!generationId && hasOpportunity,
  });

  const angles = useMemo(() => {
    if (!anglesData) return [];
    let filtered = anglesData.filter((a) => a.status === "active");
    if (opportunityTitle) {
      filtered = filtered.filter((a) => a.notes === opportunityTitle);
    }
    return filtered;
  }, [anglesData, opportunityTitle]);

  const toggleAngle = (angle: NarrativeAngle) => {
    const isSelected = selectedAngles.some((a) => a.id === angle.id);
    if (isSelected) {
      onSelect(selectedAngles.filter((a) => a.id !== angle.id));
    } else if (selectedAngles.length < MAX_ANGLES) {
      onSelect([...selectedAngles, angle]);
    }
  };

  return (
    <div className="space-y-6" data-testid="step-narrative-angles">
      <div>
        <h2 className="text-xl font-bold tracking-tight" data-testid="text-step-title">
          Narrative Angles
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          Select up to {MAX_ANGLES} narrative angles for this campaign
        </p>
      </div>

      {!hasOpportunity ? (
        <Card className="p-6 text-center">
          <p className="text-sm text-muted-foreground">
            Select a narrative opportunity first to see available angles.
          </p>
        </Card>
      ) : isError ? (
        <Card className="p-6 text-center space-y-2">
          <AlertCircle className="h-5 w-5 text-destructive mx-auto" />
          <p className="text-sm text-muted-foreground">
            Failed to load narrative angles. Please try again.
          </p>
        </Card>
      ) : isLoading ? (
        <Card className="p-8 flex items-center justify-center gap-3">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
          <span className="text-sm text-muted-foreground">
            Loading narrative angles...
          </span>
        </Card>
      ) : angles.length === 0 ? (
        generationId && opportunityTitle ? (
          <InlineGenerateAnglesPanel
            generationId={generationId}
            opportunityTitle={opportunityTitle}
            brandId={brandId}
          />
        ) : (
          <Card className="p-6 text-center space-y-2">
            <AlertCircle className="h-5 w-5 text-muted-foreground mx-auto" />
            <p className="text-sm text-muted-foreground">
              No narrative angles available.
            </p>
          </Card>
        )
      ) : (
        <AnimatePresence mode="wait">
          <motion.div
            key="content"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="space-y-4"
          >
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    {angles.length} Angles Available
                  </span>
                  <Badge variant="secondary" className="text-[10px]">
                    {selectedAngles.length}/{MAX_ANGLES} selected
                  </Badge>
                </div>
              </div>

              {angles.map((angle, index) => {
                const isSelected = selectedAngles.some((a) => a.id === angle.id);
                const isDisabled = !isSelected && selectedAngles.length >= MAX_ANGLES;
                const avg = averageScore(angle);
                return (
                  <motion.div
                    key={angle.id}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.06 }}
                  >
                    <Card
                      className={`p-4 cursor-pointer transition-colors ${
                        isSelected
                          ? "border-primary/40 bg-primary/[0.03] ring-1 ring-primary/20"
                          : isDisabled
                          ? "opacity-50 cursor-not-allowed"
                          : "hover-elevate"
                      }`}
                      onClick={() => !isDisabled && toggleAngle(angle)}
                      data-testid={`card-angle-${angle.id}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="text-sm font-semibold" data-testid={`text-angle-title-${angle.id}`}>
                              {angle.name}
                            </h3>
                            {angle.family && (
                              <Badge
                                className={`text-[10px] border-0 ${FAMILY_COLORS[angle.family] || "bg-muted text-muted-foreground"}`}
                              >
                                {angle.family}
                              </Badge>
                            )}
                            {avg !== null && (
                              <span className={`text-xs font-semibold ${scoreColor(avg)}`}>
                                {avg}
                              </span>
                            )}
                            {angle.isFavorite && (
                              <Star className="h-3 w-3 text-amber-500 fill-amber-500" />
                            )}
                          </div>

                          {angle.summary && (
                            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                              {angle.summary}
                            </p>
                          )}

                          <div className="grid grid-cols-2 gap-2 mt-3">
                            {angle.coreTension && (
                              <div className="flex items-start gap-1.5">
                                <Lightbulb className="h-3 w-3 text-muted-foreground mt-0.5 shrink-0" />
                                <div>
                                  <span className="text-[10px] text-muted-foreground">Core Tension</span>
                                  <p className="text-[11px] line-clamp-2">{angle.coreTension}</p>
                                </div>
                              </div>
                            )}
                            {angle.beliefShift && (
                              <div className="flex items-start gap-1.5">
                                <Target className="h-3 w-3 text-muted-foreground mt-0.5 shrink-0" />
                                <div>
                                  <span className="text-[10px] text-muted-foreground">Belief Shift</span>
                                  <p className="text-[11px] line-clamp-2">{angle.beliefShift}</p>
                                </div>
                              </div>
                            )}
                          </div>

                          <div className="flex items-center gap-3 mt-2">
                            {angle.resonanceScore !== null && (
                              <div className="flex items-center gap-1">
                                <div className="h-1 w-8 rounded-full bg-muted/30 overflow-hidden">
                                  <div className="h-full bg-primary/40 rounded-full" style={{ width: `${angle.resonanceScore}%` }} />
                                </div>
                                <span className="text-[9px] text-muted-foreground">Resonance</span>
                              </div>
                            )}
                            {angle.proofScore !== null && (
                              <div className="flex items-center gap-1">
                                <div className="h-1 w-8 rounded-full bg-muted/30 overflow-hidden">
                                  <div className="h-full bg-primary/40 rounded-full" style={{ width: `${angle.proofScore}%` }} />
                                </div>
                                <span className="text-[9px] text-muted-foreground">Proof</span>
                              </div>
                            )}
                            {angle.distinctivenessScore !== null && (
                              <div className="flex items-center gap-1">
                                <div className="h-1 w-8 rounded-full bg-muted/30 overflow-hidden">
                                  <div className="h-full bg-primary/40 rounded-full" style={{ width: `${angle.distinctivenessScore}%` }} />
                                </div>
                                <span className="text-[9px] text-muted-foreground">Distinct</span>
                              </div>
                            )}
                            {angle.emotionalScore !== null && (
                              <div className="flex items-center gap-1">
                                <div className="h-1 w-8 rounded-full bg-muted/30 overflow-hidden">
                                  <div className="h-full bg-primary/40 rounded-full" style={{ width: `${angle.emotionalScore}%` }} />
                                </div>
                                <span className="text-[9px] text-muted-foreground">Emotion</span>
                              </div>
                            )}
                          </div>
                        </div>

                        <div
                          className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border transition-colors ${
                            isSelected
                              ? "bg-primary text-primary-foreground border-primary"
                              : "border-muted-foreground/30"
                          }`}
                        >
                          {isSelected && <Check className="h-3 w-3" />}
                        </div>
                      </div>
                    </Card>
                  </motion.div>
                );
              })}
            </div>

            {selectedAngles.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <Card className="p-4 border-primary/20 bg-primary/[0.02]" data-testid="card-selected-angles-summary">
                  <div className="flex items-center gap-2 mb-2">
                    <MessageSquare className="h-3.5 w-3.5 text-primary" />
                    <span className="text-xs font-semibold">
                      {selectedAngles.length} Angle{selectedAngles.length !== 1 ? "s" : ""} Selected
                    </span>
                  </div>
                  <div className="space-y-1">
                    {selectedAngles.map((a) => {
                      const avg = averageScore(a);
                      return (
                        <div key={a.id} className="flex items-center gap-2">
                          <div className="h-1 w-1 rounded-full bg-primary/50" />
                          <span className="text-xs">{a.name}</span>
                          {avg !== null && (
                            <span className="text-[10px] text-muted-foreground">({avg})</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </Card>
              </motion.div>
            )}
          </motion.div>
        </AnimatePresence>
      )}
    </div>
  );
}
